import { createContext, useContext, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Lets a detail page tell the layout what to call the record it is showing, so breadcrumbs read
 * "Leasing > Lease LA-1042 · Jane Doe" instead of exposing a raw id.
 */
interface BreadcrumbContextType {
  labels: Record<string, string>;
  setLabel: (path: string, label: string | null) => void;
}

export const BreadcrumbContext = createContext<BreadcrumbContextType | undefined>(undefined);

function useBreadcrumbContext(): BreadcrumbContextType {
  const context = useContext(BreadcrumbContext);
  if (context === undefined) {
    throw new Error('useBreadcrumbs must be used within a BreadcrumbProvider');
  }
  return context;
}

/** Read the registered labels (used by the layout that renders the breadcrumbs). */
export function useBreadcrumbLabels(): Record<string, string> {
  return useBreadcrumbContext().labels;
}

/**
 * Register a friendly name for the page currently being viewed. Pass null/undefined while the
 * record is still loading; the label is cleared when the page unmounts.
 */
export function useBreadcrumbLabel(label: string | null | undefined) {
  const { setLabel } = useBreadcrumbContext();
  const { pathname } = useLocation();

  useEffect(() => {
    setLabel(pathname, label ?? null);
    return () => setLabel(pathname, null);
  }, [pathname, label, setLabel]);
}
